import {
  Box,
  Checkbox,
  FormGroup,
  Grid2,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { t } from 'react-i18nify';
import { makeStyles } from 'tss-react/mui';
import { black, darkBlue, yellow } from '../Constants/Colors';
import {
  NO_RAID_LEVEL_OPTION,
  SKIP_AMOUNT_UPDATE
} from '../Constants/Constants';
import { useAppDispatch } from '../hooks';
import {
  IActiveFilterValues,
  IComponent,
  IOption,
  TSlotInfo
} from '../Interface/Interface';
import {
  setOptionInputText,
  switchOptions
} from '../Reducers/ConfigurationSlice';
import {
  findSelectedOptionAmount,
  isOptionSelected
} from '../Utils/FindInConfiguration';
import { isSmartPhoneSize } from '../Utils/isMobile';
import DefaultOption from './DefaultOption';
import FilterRow from './FilterRow';
import TextButton from './TextButton';

import CheckIcon from '@mui/icons-material/Check';
import { getImageUrl } from '../Utils/getImageUrl';
import { getFilteredOptions } from '../Helpers/OptionFilterHelpers';

export interface IComponentProps {
  component: IComponent;
  checkOptionDisabled?: IOptionCheckFunc;
  optionLabel?: IOptionLabelFunc;
  disableAmountSelection?: boolean;
  allowZeroAmount?: boolean;
  disableAmountIncrease?: boolean;
  hint?: string;
  optionFilterFunction?: (options: IOption[]) => IOption[];
  determineAmountFunction?: IDetermineAmountFunc;
  minAmount?: number;
  multiselect?: boolean;
  disabled?: boolean;
}

export interface IOptionCheckFunc {
  // eslint-disable-next-line no-unused-vars
  (option: IOption): boolean;
}

export interface IOptionLabelFunc {
  // eslint-disable-next-line no-unused-vars
  (option: IOption): string;
}

export interface IDetermineAmountFunc {
  (optionIdentifier: string, amount: number, selectedAmount: number): number;
}

const useStyles = makeStyles()((theme) => {
  return {
    grid: {
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
      padding: 0,
      backgroundColor: 'white',
      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(5)
      },
      '&:not(:first-of-type)': {
        marginTop: theme.spacing(2)
      }
    },
    title: {
      padding: '0 0 16px 0',
      fontSize: 16,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '24px',
      [theme.breakpoints.up('sm')]: {
        padding: '32px 16px 32px 0',
        fontSize: 26
      }
    },
    componentImage: {
      maxHeight: 36,
      [theme.breakpoints.up('sm')]: {
        maxHeight: 72
      }
    }
  };
});

const DefaultComponent: FC<IComponentProps> = ({
  component,
  hint,
  checkOptionDisabled,
  optionLabel,
  disableAmountSelection = false,
  optionFilterFunction,
  determineAmountFunction = undefined,
  minAmount = 1,
  multiselect = false,
  disabled = false
}) => {
  const { classes } = useStyles();
  const isMobile = isSmartPhoneSize();

  const { title } = component;
  let options = component.options;

  if (optionFilterFunction) {
    options = optionFilterFunction(options);
  }

  const selectedOptions = component.selectedoptions || [];

  const selectedOption = selectedOptions?.[0];

  const dispatch = useAppDispatch();

  const [activeFilters, setActiveFilters] =
    useState<IActiveFilterValues | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>(options);

  const getSelectedOption = (optionIdentifier: string) =>
    selectedOptions?.find(({ identifier }) => identifier === optionIdentifier);

  const validateAmountWithSlotInfo = (
    optionIdentifier: string,
    value: number,
    updateSkipped?: (slotInfo?: TSlotInfo) => void
  ) => {
    const option = options.find(
      ({ identifier }) => identifier === optionIdentifier
    );
    if (!option) {
      return SKIP_AMOUNT_UPDATE;
    }
    const selectedOption = selectedOptions?.find(
      ({ identifier }) => identifier === optionIdentifier
    );
    const amount = selectedOption?.amount || 0;

    if (option.slotInfoAdd?.optionCanBeSwitched === false) {
      updateSkipped && updateSkipped(option.slotInfoAdd);
      return SKIP_AMOUNT_UPDATE;
    }
    if (option.slotInfoRemove?.optionCanBeSwitched === false) {
      updateSkipped && updateSkipped(option.slotInfoRemove);
      return SKIP_AMOUNT_UPDATE;
    }

    if (
      selectedOption &&
      value > amount &&
      option.slotInfoAdd?.amountCanBeIncreased === false
    ) {
      updateSkipped && updateSkipped(option.slotInfoAdd);
      return SKIP_AMOUNT_UPDATE;
    }

    if (
      (value < amount ||
        (component.is_multiselect &&
          amount > 0 &&
          (!option?.amount_is_selectable || value === 0))) &&
      option.slotInfoRemove?.amountCanBeDecreased === false
    ) {
      updateSkipped && updateSkipped(option.slotInfoRemove);
      return SKIP_AMOUNT_UPDATE;
    }

    const maxAmount = option.slotInfoAdd?.maxAmount;
    if (maxAmount && value > maxAmount) {
      if (amount === maxAmount) {
        updateSkipped && updateSkipped(option.slotInfoAdd);
        return SKIP_AMOUNT_UPDATE;
      }
      return maxAmount;
    }

    const minAmount = option.slotInfoRemove?.minAmount;
    if (minAmount && value < minAmount) {
      if (amount === minAmount) {
        updateSkipped && updateSkipped(option.slotInfoRemove);
        return SKIP_AMOUNT_UPDATE;
      }
      return minAmount;
    }

    return value;
  };

  const validateOnClick = (
    optionIdentifier: string,
    updateSkipped?: (slotInfo?: TSlotInfo) => void
  ) => {
    const option = getSelectedOption(optionIdentifier);
    let amount = component.is_multiselect
      ? option?.amount
        ? 0
        : 1
      : selectedOption?.amount;
    amount = determineAmount(option, optionIdentifier, amount);
    return validateAmountWithSlotInfo(optionIdentifier, amount, updateSkipped);
  };

  const onOptionChange = (
    optionIdentifier: string,
    updateSkipped?: (slotInfo?: TSlotInfo) => void
  ) => {
    const option = getSelectedOption(optionIdentifier);
    let amount = component.is_multiselect
      ? option?.amount
        ? 0
        : 1
      : selectedOption?.amount;
    amount = determineAmount(option, optionIdentifier, amount);
    amount = validateAmountWithSlotInfo(
      optionIdentifier,
      amount,
      updateSkipped
    );
    amount !== SKIP_AMOUNT_UPDATE &&
      dispatch(switchOptions(component.identifier, optionIdentifier, amount));
  };

  const onOptionAmountChange = (
    optionIdentifier: string,
    amount: number,
    updateSkipped?: (slotInfo?: TSlotInfo) => void
  ) => {
    const option = getSelectedOption(optionIdentifier) || selectedOption;
    if (!option) return;
    amount = determineAmount(option, optionIdentifier, amount);
    amount = validateAmountWithSlotInfo(
      optionIdentifier,
      amount,
      updateSkipped
    );
    amount !== SKIP_AMOUNT_UPDATE &&
      dispatch(switchOptions(component.identifier, optionIdentifier, amount));
  };

  const onInputTextChange = (optionIdentifier: string, inputText: string) => {
    dispatch(
      setOptionInputText({
        componentIdentifier: component.identifier,
        optionIdentifier,
        inputText
      })
    );
  };

  const determineAmount = (
    selectedOption: IOption | undefined,
    optionIdentifier: string,
    amount: number
  ): number => {
    const selectedAmount = selectedOption?.amount || 0;
    if (determineAmountFunction) {
      return determineAmountFunction(optionIdentifier, amount, selectedAmount);
    }
    if (
      selectedOption?.identifier === optionIdentifier &&
      !selectedOption.amount_is_selectable &&
      !component.is_mandatory &&
      !component.is_multiselect
    ) {
      return 0;
    }
    const nextOption =
      selectedOption?.identifier === optionIdentifier
        ? selectedOption
        : component.options.find(
            ({ identifier }) => identifier === optionIdentifier
          );
    if (
      nextOption &&
      !nextOption.amount_is_selectable &&
      component.identifier !== 'cpu'
    ) {
      return 1;
    }

    return selectedAmount > 1 && selectedOption?.identifier !== optionIdentifier
      ? selectedAmount
      : amount;
  };

  const radioGroupValue = selectedOption?.identifier || '';

  const resetToDefault = useCallback(() => {
    setActiveFilters(null);
    setFilteredOptions(options);
  }, [options]);

  useEffect(
    () => setFilteredOptions(getFilteredOptions(options, activeFilters)),
    [activeFilters, options]
  );

  const GroupComponent = multiselect ? FormGroup : RadioGroup;
  const ControlComponent = multiselect ? Checkbox : Radio;

  const renderControl = (option: IOption) => (
    <ControlComponent
      sx={{
        padding: '0px',
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        borderRadius: 0,
        '&.Mui-checked': {
          border: `2px solid ${yellow}`
        },
        '& .MuiSvgIcon-root': {
          fontSize: 24,
          color: black,
          background: yellow
        }
      }}
      icon={<></>}
      checkedIcon={<CheckIcon />}
      {...(multiselect
        ? { checked: isOptionSelected(option, component.selectedoptions) }
        : {})}
    />
  );

  return (
    <Grid2 className={classes.grid}>
      <Typography variant="h2" className={classes.title}>
        {component.thumbnail && (
          <img
            alt={component.title}
            src={getImageUrl(component.thumbnail)}
            className={classes.componentImage}
          />
        )}
        {title}
      </Typography>
      {hint && <Typography sx={{ color: darkBlue, pb: 1 }}>{hint}</Typography>}

      {activeFilters && !isMobile && (
        <Box>
          <TextButton text={t('resetFilter')} onClick={resetToDefault} />
        </Box>
      )}

      {component.filters && component.filters.length > 0 && (
        <FilterRow
          component={component}
          onSelectedFiltersChanged={setActiveFilters}
          activeFilters={activeFilters}
          resetActiveFilters={resetToDefault}
        />
      )}

      {filteredOptions.length === 0 && (
        <Typography variant="body1" color={'red'}>
          {t('noFilterResult')}
        </Typography>
      )}

      {filteredOptions.length ? (
        <GroupComponent value={radioGroupValue} row>
          <Grid2 container spacing={2} sx={{ flexGrow: 1 }}>
            {filteredOptions.map((option, index) => {
              const amountSelectable =
                (option.amount_is_selectable ||
                  selectedOptions.find(
                    ({ identifier }) => identifier === option.identifier
                  )?.amount_is_selectable) &&
                !disableAmountSelection;
              return (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                  key={option.identifier}>
                  <DefaultOption
                    onChange={onOptionChange}
                    key={index}
                    option={option}
                    optionLabel={optionLabel}
                    disabled={
                      disabled ||
                      option.incompatible ||
                      option.slotInfoAdd?.optionCanBeSwitched === false ||
                      option.slotInfoRemove?.optionCanBeSwitched === false ||
                      (checkOptionDisabled && checkOptionDisabled(option)) ||
                      (component.disabled &&
                        option.identifier !== NO_RAID_LEVEL_OPTION)
                    }
                    amountSelectable={amountSelectable}
                    onAmountChanged={onOptionAmountChange}
                    amount={findSelectedOptionAmount(option, selectedOptions)}
                    minAmount={minAmount}
                    amountSteps={component.amountSteps}
                    control={renderControl(option)}
                    validateOnClick={validateOnClick}
                    onInputTextChange={onInputTextChange}
                  />
                </Grid2>
              );
            })}
          </Grid2>
        </GroupComponent>
      ) : null}
    </Grid2>
  );
};

export default DefaultComponent;
